/**
 * Badge Notification Component
 * Shows a popup when a player earns a new badge
 */

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const BadgeNotification = ({ badge, onClose, autoClose = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 100);

    // Auto close after 5 seconds
    if (autoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [autoClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!badge) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible && !isExiting ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ pointerEvents: isVisible ? 'auto' : 'none' }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Badge Card */}
      <div 
        className={`relative bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center transform transition-all duration-500 ${
          isVisible && !isExiting ? 'scale-100 translate-y-0' : 'scale-75 translate-y-10'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>

        {/* Celebration burst */}
        <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-gradient-to-b from-yellow-200/50 to-transparent rounded-full blur-2xl" />
        </div>

        {/* Content */}
        <div className="relative">
          {/* Stars decoration */}
          <div className="text-2xl mb-2 animate-pulse">
            ✨ 🌟 ✨
          </div>

          {/* NEW BADGE text */}
          <div className="text-sm font-bold text-purple-600 tracking-widest mb-4">
            NEW BADGE EARNED!
          </div>

          {/* Badge emoji */}
          <div 
            className="text-8xl mb-4 animate-bounce"
            style={{ animationDuration: '1s' }}
          >
            {badge.emoji}
          </div>

          {/* Badge name */}
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {badge.name}
          </h2>

          {/* Badge description */}
          <p className="text-gray-600 mb-4">
            {badge.description}
          </p>

          {/* Points earned */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full text-white font-bold">
            +{badge.points} Points! 🎉
          </div>
        </div>

        {/* Tap to close hint */}
        <p className="text-xs text-gray-400 mt-6">
          Tap anywhere to continue
        </p>
      </div>

      <style>{`
        @keyframes badgeShine {
          0% { transform: translateX(-100%) rotate(45deg); }
          100% { transform: translateX(100%) rotate(45deg); }
        }
      `}</style>
    </div>
  );
};

export default BadgeNotification;
